import React from "react";

/*
  대시보드 개편안 공용 조각.

  카드 테두리·모서리·그림자를 여기 한 곳에서만 정한다. 카드마다 제각각 적어두면
  화면이 "직접 만든 티"가 난다(모서리 18/24/30px 이 섞여 있던 게 예전 문제).

  아이콘은 전부 선(stroke) 아이콘이다. 이모지를 쓰면 OS·브라우저마다 다르게 그려지고
  크기·정렬을 맞출 수 없다.
*/

// 그림자를 쓰지 않는다. 카드마다 그림자가 깔리면 화면이 둥둥 떠 보이고
// 템플릿으로 찍어낸 느낌이 난다. 경계는 1px 실선 하나면 충분하다.
export const CARD =
  "rounded-xl border border-slate-200 bg-white dark:border-white/10 dark:bg-[#111827]/80";

export function Card({ className = "", children, ...rest }) {
  return (
    <div className={`${CARD} ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function CardTitle({ icon, title, right, className = "" }) {
  return (
    <div
      className={`flex h-[50px] shrink-0 items-center gap-2 border-b border-slate-200 px-4 dark:border-white/10 ${className}`}
    >
      {icon}
      <h3 className="text-[14px] font-semibold tracking-[-0.01em] text-slate-900 dark:text-white">
        {title}
      </h3>
      {right ? <div className="ml-auto flex items-center gap-2">{right}</div> : null}
    </div>
  );
}

// 작은 상태 알약. tone: ok | warn | danger | neutral | brand
const TONES = {
  ok: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
  warn: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
  danger: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300",
  neutral: "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300",
  brand: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300",
};

export function Tag({ tone = "neutral", className = "", children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-[7px] py-[2px] text-[11.5px] font-semibold ${
        TONES[tone] || TONES.neutral
      } ${className}`}
    >
      {children}
    </span>
  );
}

export function Dot({ tone = "ok", className = "" }) {
  const color =
    tone === "ok"
      ? "bg-emerald-500 ring-emerald-100 dark:ring-emerald-500/20"
      : tone === "danger"
      ? "bg-rose-500 ring-rose-100 dark:ring-rose-500/20"
      : tone === "warn"
      ? "bg-amber-500 ring-amber-100 dark:ring-amber-500/20"
      : "bg-slate-300 ring-slate-100 dark:bg-slate-600 dark:ring-white/10";
  return (
    <span className={`inline-block h-[6px] w-[6px] rounded-full ring-[3px] ${color} ${className}`} />
  );
}

// ── 아이콘 ────────────────────────────────────────────────────────────────
function Svg({ size = 18, className = "", children }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const Icon = {
  Camera: (p) => (
    <Svg {...p}>
      <path d="M15 10.5V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-3.5l5 3.5V7z" />
    </Svg>
  ),
  Alert: (p) => (
    <Svg {...p}>
      <path d="M12 3.5 2.5 20h19z" />
      <path d="M12 10v4.5M12 17.4v.1" />
    </Svg>
  ),
  Clock: (p) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.2V12l3.2 2" />
    </Svg>
  ),
  Check: (p) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.3l2.4 2.4 4.6-4.9" />
    </Svg>
  ),
  Film: (p) => (
    <Svg {...p}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.2" />
      <path d="M8 5v14M16 5v14M2.5 12h19" />
    </Svg>
  ),
  Flag: (p) => (
    <Svg {...p}>
      <path d="M5 21V4M5 4h12l-2.5 4L17 12H5" />
    </Svg>
  ),
  Left: (p) => (
    <Svg strokeWidth="2.2" {...p}>
      <path d="M15 5l-7 7 7 7" />
    </Svg>
  ),
  Right: (p) => (
    <Svg strokeWidth="2.2" {...p}>
      <path d="M9 5l7 7-7 7" />
    </Svg>
  ),
  Up: (p) => (
    <Svg strokeWidth="2.8" {...p}>
      <path d="M12 19V5M6 11l6-6 6 6" />
    </Svg>
  ),
  Down: (p) => (
    <Svg strokeWidth="2.8" {...p}>
      <path d="M12 5v14M18 13l-6 6-6-6" />
    </Svg>
  ),
  Chart: (p) => (
    <Svg {...p}>
      <path d="M3 20h18" />
      <path d="M6 16V9M11 16V5M16 16v-4" />
    </Svg>
  ),
  Grid: (p) => (
    <Svg {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1.6" />
      <rect x="14" y="3" width="7" height="7" rx="1.6" />
      <rect x="3" y="14" width="7" height="7" rx="1.6" />
      <rect x="14" y="14" width="7" height="7" rx="1.6" />
    </Svg>
  ),
  Play: (p) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5l6 3.5-6 3.5z" />
    </Svg>
  ),
  Plus: (p) => (
    <Svg {...p}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  ),
  Image: (p) => (
    <Svg {...p}>
      <rect x="3" y="4.5" width="18" height="15" rx="2.2" />
      <circle cx="8.5" cy="10" r="1.6" />
      <path d="M21 16l-5-5-9 8.5" />
    </Svg>
  ),
};
